/**
 * API Configuration
 * 
 * Contains constants and configuration for API requests.
 * Auth: authFetch and axios use authManager for single-flight refresh on 401; no event dispatch.
 */

import axios from 'axios';
import { isWorkOSEnabled, getAuthHeaders } from './workosAuth';
import { handle401 } from './authManager';

// Base URL for API requests (single source: frontend/src/config.ts)
import { API_BASE_URL } from '../config';
export { API_BASE_URL };

/** @deprecated No longer dispatched; auth is centralized in authManager. Kept for tests that assert it is not used. */
export const AUTH_401_EVENT = 'auth:401';

/**
 * Authenticated fetch. On 401: runs single-flight refresh via authManager; retries request once if refresh succeeds.
 * Does not clear tokens or redirect; authManager invokes session-invalidated callback on refresh failure.
 * Pass noRedirect: true to skip refresh/retry (caller handles 401).
 */
export async function authFetch(
  input: RequestInfo | URL,
  init?: RequestInit & { noRedirect?: boolean }
): Promise<Response> {
  const doRequest = async (): Promise<Response> => {
    const headers = new Headers(init?.headers);
    const authHeaders = await getAuthHeaders();
    const auth = authHeaders && typeof authHeaders === 'object' && 'Authorization' in authHeaders ? (authHeaders as { Authorization: string }).Authorization : null;
    if (auth) headers.set('Authorization', auth);
    return fetch(input, { ...init, credentials: init?.credentials ?? 'include', headers });
  };

  let res = await doRequest();
  if (res.status === 401 && !init?.noRedirect && typeof window !== 'undefined') {
    const recovered = await handle401();
    if (recovered) res = await doRequest();
    else throw new Error('Unauthorized');
  }
  if (res.status === 401 && !init?.noRedirect) throw new Error('Unauthorized');
  return res;
}

// Default request timeout in milliseconds
export const DEFAULT_TIMEOUT = 30000;

// Default headers for all requests
export const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
  'Accept': 'application/json',
};

// Configure axios defaults
axios.defaults.baseURL = API_BASE_URL;
axios.defaults.timeout = DEFAULT_TIMEOUT;
axios.defaults.withCredentials = true;
axios.defaults.headers.common = {
  ...axios.defaults.headers.common,
  ...DEFAULT_HEADERS,
};

// Add request interceptor for authentication. Uses getAuthHeaders() so Bearer is sent (including prod fallback from sessionStorage).
axios.interceptors.request.use(
  async (config) => {
    const authHeaders = await getAuthHeaders();
    const auth = authHeaders && typeof authHeaders === 'object' && 'Authorization' in authHeaders
      ? (authHeaders as { Authorization: string }).Authorization
      : null;
    if (auth) config.headers.Authorization = auth;

    // Add project context header if available
    const projectId = localStorage.getItem('currentProjectId');
    if (projectId) {
      config.headers['x-project-id'] = projectId;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor: 401 → single-flight refresh via authManager, then retry once
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401 && isWorkOSEnabled() && typeof window !== 'undefined') {
      const { handle401 } = await import('./authManager');
      const recovered = await handle401();
      if (recovered && error.config) {
        const authHeaders = await getAuthHeaders();
        const auth = authHeaders && typeof authHeaders === 'object' && 'Authorization' in authHeaders ? (authHeaders as { Authorization: string }).Authorization : null;
        if (auth && error.config.headers) {
          const h = error.config.headers as Record<string, string>;
          h.Authorization = auth;
        }
        return axios.request(error.config);
      }
    }
    if (error.response?.status === 401 && !isWorkOSEnabled()) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
); 